import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, ClipboardPaste, Pencil, X, Clock, CalendarRange, Eraser, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatTime, getTimeFormat, setTimeFormat } from "@/lib/time";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const EVENT_CATS = [
  { k: "job_fair", label: "Career / Job fair" },
  { k: "office_hours", label: "Office hours" },
  { k: "meeting", label: "Meeting / advising" },
  { k: "study_group", label: "Study group" },
  { k: "exam", label: "Exam / review" },
  { k: "other", label: "Other" },
];
const EVENT_LABEL = Object.fromEntries(EVENT_CATS.map((c) => [c.k, c.label]));
const EMPTY = { name: "", kind: "class", category: "", event_date: "", booking_url: "", lat: null, lng: null, location: "", building_id: "", room: "", days: [], start_time: "10:20", end_time: "11:10" };

export default function ScheduleBuilder({ buildings, classes, reload, semester, onSemester }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [timeFmt, setTimeFmt] = useState(getTimeFormat());
  const [form, setForm] = useState(EMPTY);
  const [semOpen, setSemOpen] = useState(false);
  const [sem, setSem] = useState({ name: "", start_date: "", end_date: "" });

  useEffect(() => {
    if (semester) setSem({ name: semester.name || "", start_date: semester.start_date || "", end_date: semester.end_date || "" });
  }, [semester]);

  // Pre-fill from an "Add to my events" tap on the Places page (draft in localStorage).
  useEffect(() => {
    const raw = localStorage.getItem("routerev_event_draft");
    if (!raw) return;
    localStorage.removeItem("routerev_event_draft");
    try {
      const d = JSON.parse(raw);
      setForm({ ...EMPTY, kind: "event", category: d.category || "other", ...d });
      setEditingId(null);
      setOpen(true);
      setShowImport(false);
      toast.info("Review the time & save your event");
    } catch { /* ignore bad draft */ }
  }, []);

  // Open the event form from anywhere (e.g. the dashboard appointments strip).
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      setForm({ ...EMPTY, kind: "event", category: d.category || "job_fair", ...d });
      setEditingId(null);
      setOpen(true);
      setShowImport(false);
    };
    window.addEventListener("routerev:add-event", handler);
    return () => window.removeEventListener("routerev:add-event", handler);
  }, []);

  const saveSemester = async () => {
    const res = await api.put("/semester", sem);
    toast.success("Semester dates saved");
    setSemOpen(false);
    onSemester?.(res.data);
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete all ${classes.length} classes? This can't be undone.`)) return;
    const res = await api.delete("/classes");
    toast.success(`Cleared ${res.data.deleted} classes — ready for a new semester`);
    resetForm();
    reload();
  };

  const fmtDate = (d) => (d ? new Date(d + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

  const cycleFmt = () => {
    const next = timeFmt === "12" ? "24" : "12";
    setTimeFmt(next);
    setTimeFormat(next);
  };

  const toggleDay = (d) =>
    setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
    setOpen(false);
  };

  const startAdd = () => {
    if (open && !editingId) { setOpen(false); return; }
    setForm(EMPTY);
    setEditingId(null);
    setOpen(true);
    setShowImport(false);
  };

  const startAddEvent = () => {
    if (open && !editingId && form.kind === "event") { setOpen(false); return; }
    setForm({ ...EMPTY, kind: "event", category: "job_fair" });
    setEditingId(null);
    setOpen(true);
    setShowImport(false);
  };

  const startEdit = (c) => {
    setForm({
      name: c.name, kind: c.kind || "class", category: c.category || "", event_date: c.event_date || "", booking_url: c.booking_url || "",
      lat: c.lat ?? null, lng: c.lng ?? null, location: c.location || "",
      building_id: c.building_id || "", room: c.room || "", days: c.days || [],
      start_time: c.start_time, end_time: c.end_time,
    });
    setEditingId(c.id);
    setOpen(true);
    setShowImport(false);
  };

  const submit = async () => {
    if (!form.name || (!form.building_id && form.lat == null) || form.days.length === 0) {
      toast.error("Add a name, location, and at least one day");
      return;
    }
    if (editingId) {
      await api.put(`/classes/${editingId}`, form);
      toast.success(form.kind === "event" ? "Event updated" : "Class updated");
    } else {
      await api.post("/classes", form);
      toast.success(form.kind === "event" ? "Event added to your schedule" : "Class added");
    }
    resetForm();
    reload();
  };

  const del = async (id) => {
    await api.delete(`/classes/${id}`);
    if (editingId === id) resetForm();
    reload();
  };

  const doImport = async () => {
    if (!importText.trim()) return;
    const res = await api.post("/classes/import", { text: importText });
    toast.success(`Imported ${res.data.count} classes`);
    setImportText("");
    setShowImport(false);
    reload();
  };

  const inputCls =
    "w-full bg-white border border-border rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand";

  // Open + jump to paste-import when the term "start" alert asks the student to add classes.
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setShowImport(true);
      setTimeout(() => document.getElementById("schedule-builder")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    };
    window.addEventListener("open-schedule-builder", handler);
    return () => window.removeEventListener("open-schedule-builder", handler);
  }, []);

  return (
    <div id="schedule-builder" className="bg-white border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Your classes</div>
          <h3 className="font-display text-xl font-semibold">{classes.length} courses</h3>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="time-format-toggle"
            onClick={cycleFmt}
            title="Toggle 12h / 24h time"
            className="flex items-center gap-1 p-2.5 rounded-md border border-border hover:border-brand transition-colors mono text-xs font-bold"
          >
            <Clock size={14} strokeWidth={1.5} /> {timeFmt === "12" ? "12h" : "24h"}
          </button>
          <button
            data-testid="import-toggle-btn"
            onClick={() => setShowImport((s) => !s)}
            className="p-2.5 rounded-md border border-border hover:border-brand transition-colors"
            title="Paste schedule"
          >
            <ClipboardPaste size={16} strokeWidth={1.5} />
          </button>
          <button
            data-testid="add-event-btn"
            onClick={startAddEvent}
            title="Add a one-off event (career fair, office hours, meeting…)"
            className="flex items-center gap-1.5 bg-white border border-border hover:border-brand rounded-md px-3 py-2.5 text-sm font-semibold transition-colors"
          >
            <CalendarPlus size={16} strokeWidth={1.8} /> Event
          </button>
          <button
            data-testid="add-class-btn"
            onClick={startAdd}
            className="flex items-center gap-1.5 bg-brand text-white rounded-md px-3 py-2.5 text-sm font-semibold hover:opacity-90 transition-colors"
          >
            <Plus size={16} strokeWidth={2} /> Add
          </button>
        </div>
      </div>

      {/* Semester bar */}
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-border bg-secondary/30" data-testid="semester-bar">
        <button
          data-testid="semester-edit-btn"
          onClick={() => setSemOpen((s) => !s)}
          className="flex items-center gap-2 text-sm min-w-0 hover:text-brand transition-colors"
        >
          <CalendarRange size={15} strokeWidth={1.5} className="text-brand shrink-0" />
          <span className="truncate">
            {semester?.start_date
              ? <><b>{semester.name || "Semester"}</b> · {fmtDate(semester.start_date)} – {fmtDate(semester.end_date)}</>
              : "Set semester dates"}
          </span>
        </button>
        {classes.length > 0 && (
          <button
            data-testid="clear-schedule-btn"
            onClick={clearAll}
            className="flex items-center gap-1 text-xs font-semibold text-destructive hover:bg-destructive/10 rounded-md px-2 py-1.5 transition-colors shrink-0"
          >
            <Eraser size={13} strokeWidth={1.5} /> Clear all
          </button>
        )}
      </div>

      {semOpen && (
        <div className="p-5 border-b border-border bg-secondary/40 space-y-3" data-testid="semester-form">
          <input
            data-testid="semester-name-input"
            className={inputCls}
            placeholder="Semester name (e.g. Fall 2026)"
            value={sem.name}
            onChange={(e) => setSem({ ...sem, name: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-muted-foreground">Start
              <input data-testid="semester-start-input" type="date" className={inputCls} value={sem.start_date}
                onChange={(e) => setSem({ ...sem, start_date: e.target.value })} />
            </label>
            <label className="text-xs text-muted-foreground">End
              <input data-testid="semester-end-input" type="date" className={inputCls} value={sem.end_date}
                onChange={(e) => setSem({ ...sem, end_date: e.target.value })} />
            </label>
          </div>
          <p className="text-xs text-muted-foreground">Reminders pause automatically outside these dates (breaks, summer, etc.).</p>
          <button
            data-testid="semester-save-btn"
            onClick={saveSemester}
            className="w-full bg-brand text-white rounded-md py-2.5 text-sm font-semibold hover:opacity-90 transition-colors"
          >
            Save semester
          </button>
        </div>
      )}

      {showImport && (
        <div className="p-5 border-b border-border bg-secondary/40">
          <textarea
            data-testid="import-textarea"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={5}
            placeholder={"Paste rows like:\nMATH 251  MWF  9:10 AM - 10:00 AM  Blocker\nENGL 104  TR  11:10 AM - 12:25 PM  Heldenfels"}
            className="w-full bg-white border border-dashed border-brand/40 rounded-md p-3 text-sm mono focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <button
            data-testid="import-submit-btn"
            onClick={doImport}
            className="mt-3 w-full bg-brand text-white rounded-md py-2.5 text-sm font-semibold hover:opacity-90 transition-colors"
          >
            Parse &amp; import
          </button>
        </div>
      )}

      {open && (
        <div className="p-5 border-b border-border bg-secondary/40 space-y-3">
          <div className="flex items-center justify-between">
            <span className="mono text-[11px] tracking-[0.2em] uppercase text-brand">
              {form.kind === "event" ? (editingId ? "Edit event" : "New event") : (editingId ? "Edit class" : "New class")}
            </span>
            <button
              data-testid="class-form-cancel"
              onClick={resetForm}
              className="p-1 rounded-md hover:bg-secondary transition-colors text-muted-foreground"
            >
              <X size={15} strokeWidth={1.5} />
            </button>
          </div>
          {form.kind === "event" && (
            <select
              data-testid="event-category-select"
              className={inputCls}
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {EVENT_CATS.map((c) => (
                <option key={c.k} value={c.k}>{c.label}</option>
              ))}
            </select>
          )}
          <input
            data-testid="class-name-input"
            className={inputCls}
            placeholder={form.kind === "event" ? "Event name (e.g. Engineering Career Fair)" : "Course name (e.g. CSCE 221)"}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          {form.lat != null ? (
            <div data-testid="event-location-fixed" className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2.5 text-sm">
              <span className="mono text-[11px] uppercase tracking-widest text-brand shrink-0">Location</span>
              <span className="truncate font-semibold">{form.location || form.name}</span>
            </div>
          ) : (
            <select
              data-testid="class-building-select"
              className={inputCls}
              value={form.building_id}
              onChange={(e) => setForm({ ...form, building_id: e.target.value })}
            >
              <option value="">Select building…</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
          <input
            data-testid="class-room-input"
            className={inputCls}
            placeholder="Room # (e.g. 102 or Rm 340)"
            value={form.room}
            onChange={(e) => setForm({ ...form, room: e.target.value })}
          />
          {form.kind === "event" && (
            <label className="block text-xs text-muted-foreground">
              Date (optional — auto-hides after it passes)
              <input
                data-testid="event-date-input"
                type="date"
                className={inputCls}
                value={form.event_date}
                onChange={(e) => {
                  const v = e.target.value;
                  const upd = { ...form, event_date: v };
                  if (v) {
                    const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date(v + "T00:00").getDay()];
                    if (DAYS.includes(wd)) upd.days = [wd];
                  }
                  setForm(upd);
                }}
              />
            </label>
          )}
          <div className="flex gap-2">
            {DAYS.map((d) => (
              <button
                key={d}
                data-testid={`day-${d}`}
                onClick={() => toggleDay(d)}
                className={`flex-1 py-2 rounded-md text-xs font-bold mono transition-colors ${
                  form.days.includes(d) ? "bg-brand text-white" : "bg-white border border-border"
                }`}
              >
                {d[0]}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <input data-testid="class-start-input" type="time" className={inputCls} value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              <div data-testid="start-time-preview" className="mono text-[11px] text-muted-foreground mt-1 pl-1">
                = {form.start_time ? formatTime(form.start_time, timeFmt) : "—"}
              </div>
            </div>
            <div>
              <input data-testid="class-end-input" type="time" className={inputCls} value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
              <div data-testid="end-time-preview" className="mono text-[11px] text-muted-foreground mt-1 pl-1">
                = {form.end_time ? formatTime(form.end_time, timeFmt) : "not set"}
              </div>
            </div>
          </div>
          <button
            data-testid="class-save-btn"
            onClick={submit}
            className="w-full bg-brand text-white rounded-md py-2.5 text-sm font-semibold hover:opacity-90 transition-colors"
          >
            {editingId ? (form.kind === "event" ? "Update event" : "Update class") : (form.kind === "event" ? "Save event" : "Save class")}
          </button>
        </div>
      )}

      <div className="divide-y divide-border max-h-[320px] overflow-y-auto hide-scrollbar">
        {classes.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No classes yet. Add one or paste your schedule.
          </div>
        )}
        {classes.map((c, i) => {
          const b = buildings.find((x) => x.id === c.building_id);
          return (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              className="flex items-center gap-3 p-4 group"
            >
              <div className="mono text-xs font-bold text-brand w-20 shrink-0">
                {formatTime(c.start_time, timeFmt)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-1.5">
                  {c.kind === "event" && (
                    <span data-testid={`event-badge-${c.id}`} className="inline-flex items-center gap-1 mono text-[11px] font-bold uppercase tracking-widest text-[#92400e] bg-[#F59E0B]/15 rounded px-1.5 py-0.5 shrink-0">
                      <CalendarPlus size={10} strokeWidth={2} /> {EVENT_LABEL[c.category] || "Event"}
                    </span>
                  )}
                  <span className="truncate">{c.name}</span>
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {b?.name || c.location || "—"}{c.room ? ` · Rm ${c.room}` : ""} · {c.days.join(" ")}
                  {c.event_date ? ` · ${new Date(c.event_date + "T00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
                </div>
              </div>
              <button
                data-testid={`edit-class-${c.id}`}
                onClick={() => startEdit(c)}
                className={`p-2 rounded-md transition-all hover:bg-brand/10 hover:text-brand ${
                  editingId === c.id ? "bg-brand/10 text-brand opacity-100" : "opacity-0 group-hover:opacity-100"
                }`}
              >
                <Pencil size={15} strokeWidth={1.5} />
              </button>
              <button
                data-testid={`delete-class-${c.id}`}
                onClick={() => del(c.id)}
                className="p-2 rounded-md opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
              >
                <Trash2 size={15} strokeWidth={1.5} />
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
